
import { AnimatePresence } from "framer-motion";
import { SleepFormProvider } from "../SleepFormContext";
import { TimeStep } from "../steps/TimeStep";
import { LocationStep } from "../steps/LocationStep";
import { EnvironmentStep } from "../steps/EnvironmentStep";
import { OnsetStep } from "../steps/OnsetStep";
import { SoundStep } from "../steps/SoundStep";
import { SoundLevelStep } from "../steps/SoundLevelStep";
import { RestfulnessStep } from "../steps/RestfulnessStep";
import { SleepDebtStep } from "../steps/SleepDebtStep";
import { NotesStep } from "../steps/NotesStep";
import { StepType } from "../hooks/sleep-dialog-types";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { SleepStepRenderer } from "./SleepStepRenderer";
import { EnhancedSleepForm } from "../hooks/useSleepDialogForm";
import { SleepFormData } from "../SleepFormContext";

interface SleepDialogContentProps {
  form: EnhancedSleepForm;
  currentStep: StepType;
  showConfirmation: boolean;
  locations: { id: string; description: string }[];
  environments: { id: string; description: string }[];
  onsetMethods: { id: string; description: string }[];
  sounds: { id: string; name: string }[];
  soundLevels: { id: string; level: string }[];
  restfulnessRatings: { id: string; description: string }[];
  selectedChild?: { name: string; id: string };
  nextStep: () => void;
  prevStep: () => void;
  setShowConfirmation: (show: boolean) => void;
  onSubmit: (values: SleepFormData) => void;
}

export function SleepDialogContent({
  form,
  currentStep,
  showConfirmation,
  locations,
  environments,
  onsetMethods,
  sounds,
  soundLevels,
  restfulnessRatings,
  selectedChild,
  nextStep,
  prevStep,
  setShowConfirmation,
  onSubmit
}: SleepDialogContentProps) {
  return (
    <div className="flex-1 flex flex-col">
      {/* Confirmation Dialog */}
      {showConfirmation && (
        <ConfirmationDialog
          childName={selectedChild?.name}
          onCancel={() => setShowConfirmation(false)}
          onConfirm={() => {
            console.log('📝 [SleepDialogContent.tsx] Processing confirmed submission from content component');
            
            form.setValue("confirmed", true);
            
            // Perform validation before submitting
            form.trigger().then(isValid => {
              console.log('📝 [SleepDialogContent.tsx] Form validation result:', isValid);
              if (isValid) {
                // Get form values and log them for debugging
                const values = form.getValues();
                console.log('📝 [SleepDialogContent.tsx] Form values to be submitted:', {
                  start_time: values.start_time ? values.start_time.toString() : 'undefined',
                  end_time: values.end_time ? values.end_time.toString() : 'undefined',
                  childId: values.childId,
                  caregiverId: values.caregiverId
                });
                
                // Submit the form with the values
                onSubmit(values);
              } else {
                console.error('📝 [SleepDialogContent.tsx] Form validation failed');
              }
            });
          }}
        />
      )}
      
      <div className="flex-1 py-6">
        <div className="space-y-4">
          <SleepFormProvider value={form}>
            <SleepStepRenderer 
              currentStep={currentStep}
              selectedChild={selectedChild}
              locations={locations}
              environments={environments}
              onsetMethods={onsetMethods}
              sounds={sounds}
              soundLevels={soundLevels}
              restfulnessRatings={restfulnessRatings}
              nextStep={nextStep}
              hasWokenUp={form.watch("has_woken_up")}
            />
          </SleepFormProvider>
        </div>
      </div>
    </div>
  );
}
